import React, { useEffect, useMemo, useRef, useState } from "react";
import { AUTH_CLEARED_EVENT, authRevision, requiresFreshLogin } from "@/lib/authSession";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getAnonId } from "@/lib/anonId";
import {
  ArrowLeft, Send, ShieldCheck, Sparkles, Megaphone, Star, ImageIcon, Video, Clock,
  CheckCircle2, X, ShoppingBag, Heart, Award, ThumbsUp, MessageCircle, CalendarDays,
  MapPin, Users, Link2, Images, Plus, Loader2, Trash2, MessageSquare, Camera,
  ChevronLeft, ChevronRight, Tag, Share2, Facebook, Instagram, Twitter, CalendarPlus,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Curated brand-appropriate imagery (fallbacks for featured tiles / hero)
const HERO_BG = "https://images.unsplash.com/photo-1769798643237-8642a3fbe5bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const FEA_EVENT = "https://images.unsplash.com/photo-1762430815537-e140755eedf9?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
const FEA_TEAM = "https://images.pexels.com/photos/3993455/pexels-photo-3993455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FEA_TREND = "https://images.unsplash.com/photo-1531756108055-d6dbfd271033?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

const abs = (u) => (u && u.startsWith("/api/") ? `${API}${u}` : u);

const detectChannel = (v) => ((v || "").includes("@") ? "email" : "sms");
const toE164 = (v) => {
  const s = (v || "").trim();
  if (s.startsWith("+")) return s.replace(/[^\d+]/g, "");
  const digits = s.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const fmtEventDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const dayBadge = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return { m: "", day: "" };
  return { m: d.toLocaleString(undefined, { month: "short" }).toUpperCase(), day: d.getDate() };
};
const shortDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" }).toUpperCase();
};

const communityHeaders = () => {
  const tok = localStorage.getItem("client_token") || localStorage.getItem("access_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

async function uploadMedia(file, onProgress) {
  const CHUNK = 5 * 1024 * 1024;
  // Small files (all images, short clips) go in a single stateless request —
  // no multi-request session to lose across workers/replicas.
  if (file.size <= CHUNK) {
    onProgress?.(50);
    const fd = new FormData();
    fd.append("file", file, file.name);
    const { data } = await axios.post(`${API}/api/essence/upload`, fd, { headers: communityHeaders(), withCredentials: true });
    onProgress?.(100);
    return data;
  }
  // Larger files: MongoDB-backed chunked upload (replica-safe).
  const { data: init } = await axios.post(`${API}/api/essence/upload/init`, {}, { headers: communityHeaders(), withCredentials: true });
  const uploadId = init.upload_id;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", i);
    fd.append("chunk", blob, "chunk");
    await axios.post(`${API}/api/essence/upload/chunk`, fd, { headers: communityHeaders(), withCredentials: true });
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const fd2 = new FormData();
  fd2.append("upload_id", uploadId);
  fd2.append("filename", file.name);
  const { data: done } = await axios.post(`${API}/api/essence/upload/complete`, fd2, { headers: communityHeaders(), withCredentials: true });
  return done;
}

// ---------- Share buttons (FB / X / Instagram-copy) ----------
function ShareButtons({ text, size = "sm" }) {
  const shareUrl = `${window.location.origin}/essence`;
  const caption = `${(text || "Check out the MPIRE community!").slice(0, 180)} — via MPIRE Distribution`;
  const pop = (url) => window.open(url, "_blank", "noopener,noreferrer,width=600,height=540");
  const iconSz = size === "sm" ? 15 : 17;
  const btn = "inline-flex items-center justify-center w-8 h-8 rounded-full border border-[var(--border)] text-gray-500 transition-all active:scale-90";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="share-buttons">
      <button type="button" title="Share on Facebook" onClick={() => pop(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`)} className={`${btn} hover:border-blue-500 hover:text-blue-600`} data-testid="share-facebook"><Facebook size={iconSz} /></button>
      <button type="button" title="Share on X" onClick={() => pop(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`)} className={`${btn} hover:border-black hover:text-black`} data-testid="share-x"><Twitter size={iconSz} /></button>
      <button type="button" title="Copy for Instagram" onClick={async () => { try { await navigator.clipboard.writeText(`${caption} ${shareUrl}`); toast.success("Copied! Paste into your Instagram post or story."); } catch { toast.error("Couldn't copy — long-press to copy manually."); } }} className={`${btn} hover:border-pink-500 hover:text-pink-600`} data-testid="share-instagram"><Instagram size={iconSz} /></button>
    </div>
  );
}

// ---------- RSVP modal ----------
function RsvpModal({ event, onClose, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/events/${event.id}/rsvp`, { name: name.trim(), email: email.trim() });
      toast.success(data.already ? "You're already on the list!" : "You're in — see you there! 🎉");
      onDone?.(data.rsvp_count);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "RSVP failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose} data-testid="rsvp-modal">
      <Card className="relative w-full max-w-sm p-5 space-y-3 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700" data-testid="rsvp-close"><X size={16} /></button>
        <div className="flex items-center gap-2"><CalendarDays size={16} className="text-[var(--brand)]" /><p className="font-bold text-sm">RSVP · {event.title}</p></div>
        <p className="text-xs text-gray-500">{fmtEventDate(event.start_datetime)} · {event.location}</p>
        <form onSubmit={submit} className="space-y-2.5">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="rsvp-name" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" data-testid="rsvp-email" />
          <Button type="submit" disabled={busy} className="btn-brand w-full" data-testid="rsvp-submit">{busy ? "Reserving…" : "Reserve my spot"}</Button>
        </form>
      </Card>
    </div>
  );
}

// ---------- Inline client login ----------
function InlineClientLogin({ onLoggedIn, onClose }) {
  const { loginClient } = useAuth();
  const [step, setStep] = useState("identifier");
  const [raw, setRaw] = useState("");
  const [channel, setChannel] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [masked, setMasked] = useState("");
  const [devCode, setDevCode] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    if (!raw.trim()) { toast.error("Enter email or phone"); return; }
    const ch = detectChannel(raw);
    const ident = ch === "email" ? raw.trim().toLowerCase() : toE164(raw);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/otp/request`, { identifier: ident, channel: ch });
      setChannel(ch); setIdentifier(ident); setMasked(data.masked || "");
      if (data.dev_code) setDevCode(data.dev_code);
      setStep("code");
      toast.success(`Code sent to ${data.masked || ident}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't send code"); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Enter the numeric code"); return; }
    setBusy(true);
    try {
      const data = await loginClient({ identifier, channel, code: code.trim() });
      toast.success(`Welcome back${data.name ? `, ${data.name.split(" ")[0]}` : ""}`);
      onLoggedIn(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 border-2 border-[var(--brand)]/30 bg-gradient-to-br from-[var(--brand)]/5 to-white" data-testid="inline-client-login">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center shrink-0">
          <ShieldCheck size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-sm">Sign in to join the conversation</p>
            {onClose && <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="inline-login-close"><X size={14} /></button>}
          </div>
          {step === "identifier" ? (
            <form onSubmit={send} className="flex flex-col sm:flex-row gap-2" data-testid="inline-login-identifier">
              <Input autoFocus value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="Email or phone on file" data-testid="inline-login-input" className="flex-1" />
              <Button type="submit" disabled={busy} className="btn-brand" data-testid="inline-login-send">
                <Send size={13} className="mr-1" /> {busy ? "Sending…" : "Send code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-2" data-testid="inline-login-code">
              <p className="text-xs text-gray-600">Code sent to <span className="font-medium">{masked || identifier}</span></p>
              {devCode && (
                <div className="rounded border border-dashed border-amber-400 bg-amber-50 p-1.5 text-[11px]" data-testid="inline-dev-code">
                  Dev — code: <code className="font-mono font-bold">{devCode}</code>
                </div>
              )}
              <div className="flex gap-2">
                <Input autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="inline-code-input" className="flex-1" />
                <Button type="submit" disabled={busy} className="btn-brand" data-testid="inline-verify">{busy ? "…" : "Verify"}</Button>
                <Button type="button" variant="outline" onClick={() => { setStep("identifier"); setCode(""); setDevCode(""); }} data-testid="inline-back">Back</Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------- Post composer (tabs: Thought / Photo / Video) ----------
function ComposeForm({ onPosted }) {
  const [postType, setPostType] = useState("thought");
  const [content, setContent] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [location, setLocation] = useState("");
  const [eventTag, setEventTag] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const tabs = [
    { id: "thought", label: "Thought", icon: MessageSquare },
    { id: "photo", label: "Photo", icon: Camera },
    { id: "video", label: "Video", icon: Video },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !linkUrl.trim() && !file) { toast.error("Write something or attach media"); return; }
    setBusy(true);
    try {
      let media_url = null, media_type = null, large_video = false;
      if (file) {
        setProgress(1);
        const up = await uploadMedia(file, setProgress);
        media_url = up.media_url; media_type = up.media_type; large_video = up.large;
      }
      const { data } = await axios.post(
        `${API}/api/essence/posts`,
        { content: content.trim(), link_url: linkUrl.trim() || null, media_url, media_type, large_video, location: location.trim() || null, event_tag: eventTag.trim() || null },
        { headers: communityHeaders(), withCredentials: true }
      );
      toast.success(data.message || "Posted!");
      setContent(""); setLinkUrl(""); setLocation(""); setEventTag(""); setFile(null); setProgress(0); setPostType("thought");
      onPosted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 sm:p-5 rounded-2xl border border-[#B4B8C0] shadow-sm space-y-3.5" data-testid="post-composer">
      {/* Post-type tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = postType === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setPostType(t.id); setFile(null); }}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${active ? "bg-[var(--brand)] text-white shadow" : "bg-[var(--bg-soft)] text-[var(--ink-soft)] hover:text-[var(--brand)]"}`}
              data-testid={`composer-tab-${t.id}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={submit} className="space-y-3">
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="What's on your mind, MPire fam?" data-testid="composer-content" className="resize-none border-[#B4B8C0]/70" />

        {postType === "video" && (
          <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Paste a YouTube / Vimeo link (preferred for long videos)" data-testid="composer-link" className="border-[#B4B8C0]/70" />
        )}
        {(postType === "photo" || postType === "video") && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept={postType === "video" ? "video/mp4,video/webm,video/quicktime" : "image/*"}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[var(--brand)]/10 file:text-[var(--brand)] file:text-xs file:font-semibold"
              data-testid="post-file-input"
            />
            {file && <p className="text-[11px] text-gray-500 mt-1">{file.name} · {(file.size / 1048576).toFixed(1)} MB{file.size > 50 * 1048576 ? " — will require HQ approval" : ""}</p>}
            {busy && progress > 0 && (
              <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-[var(--brand)] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="relative">
            <MapPin size={13} className="absolute left-2.5 top-3 text-gray-400" />
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (optional)" className="pl-8 border-[#B4B8C0]/70" data-testid="composer-location" />
          </div>
          <div className="relative">
            <Tag size={13} className="absolute left-2.5 top-3 text-gray-400" />
            <Input value={eventTag} onChange={(e) => setEventTag(e.target.value)} placeholder="Event tag (optional)" className="pl-8 border-[#B4B8C0]/70" data-testid="composer-event-tag" />
          </div>
        </div>

        <Button type="submit" disabled={busy} className="btn-brand w-full h-11 rounded-lg font-semibold" data-testid="composer-submit">
          {busy ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> {progress > 0 && progress < 100 ? `Uploading ${progress}%` : "Posting…"}</> : <><Send size={15} className="mr-1.5" /> Post</>}
        </Button>
      </form>
    </Card>
  );
}

// ---------- Comments ----------
function Comments({ post, identity, onNeedLogin, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const comments = post.comments || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!identity?.verified) { onNeedLogin(); return; }
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/essence/${post.id}/comments`, { content: text.trim() }, { headers: communityHeaders(), withCredentials: true });
      setText("");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't comment"); }
    finally { setBusy(false); }
  };

  const del = async (cid) => {
    try {
      await axios.delete(`${API}/api/essence/comments/${cid}`, { headers: communityHeaders(), withCredentials: true });
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't remove"); }
  };

  return (
    <div className="pt-3 mt-1 border-t border-[var(--border)] space-y-2.5" data-testid={`comments-${post.id}`}>
      {comments.map((c) => (
        <div key={c.id} className="flex items-start gap-2 group" data-testid={`comment-${c.id}`}>
          <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-[11px] font-bold shrink-0 overflow-hidden">
            {c.author_photo ? <img src={abs(c.author_photo)} alt="" className="w-full h-full object-cover" /> : (c.author_name || "?")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs leading-snug"><span className="font-semibold capitalize">{c.author_name}</span> <span className="text-gray-400 text-[10px]">· {timeAgo(c.created_at)}</span></p>
            <p className="text-sm text-[var(--ink)] break-words">{c.content}</p>
          </div>
          {identity?.verified && (identity.is_staff || c.author_id === identity.id) && (
            <button onClick={() => del(c.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-opacity" data-testid={`delete-comment-${c.id}`}><Trash2 size={13} /></button>
          )}
        </div>
      ))}
      <form onSubmit={submit} className="flex items-center gap-2 pt-1">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={identity?.verified ? "Write a comment…" : "Sign in to comment"}
          onFocus={() => { if (!identity?.verified) onNeedLogin(); }}
          className="flex-1 h-9 text-sm"
          data-testid={`comment-input-${post.id}`}
        />
        <Button type="submit" size="sm" disabled={busy || !identity?.verified} className="btn-brand h-9" data-testid={`comment-submit-${post.id}`}>
          <Send size={13} />
        </Button>
      </form>
    </div>
  );
}

// ---------- Post card ----------
function PostCard({ post, adminMode, onApprove, onReject, onReact, onFeature, isHQ, identity, onNeedLogin, onChanged }) {
  const isAnnouncement = post.kind === "announcement";
  const isReview = post.kind === "review";
  const [showComments, setShowComments] = useState(false);
  const r = post.reactions || { heart: 0, up: 0, my_heart: false, my_up: false };
  const mediaSrc = abs(post.media_url);

  const tag = isAnnouncement
    ? { label: "News", cls: "bg-red-600 text-white", icon: Megaphone }
    : isReview
    ? { label: "Review", cls: "bg-[var(--brand)]/10 text-[var(--brand)]", icon: Star }
    : { label: "Community", cls: "bg-[#D4AF37]/15 text-[#8a6d16]", icon: Sparkles };
  const TagIcon = tag.icon;

  return (
    <Card
      className={`p-4 sm:p-5 space-y-3.5 relative rounded-xl border border-[#B4B8C0] shadow-sm hover:border-[var(--brand)]/30 transition-colors ${isAnnouncement ? "border-l-4 border-l-amber-500" : ""} ${post.is_featured ? "ring-2 ring-[#D4AF37] shadow-lg" : ""}`}
      data-testid={`post-${post.id}`}
    >
      {post.is_featured && (
        <div className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#0F0F10] text-[10px] font-bold uppercase tracking-wider shadow" data-testid={`featured-badge-${post.id}`}>
          <Award size={10} /> Featured
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {post.author_photo ? (
            <img src={abs(post.author_photo)} alt={post.author_name} className="w-10 h-10 rounded-full object-cover shrink-0 border border-[var(--border)]" />
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isAnnouncement ? "bg-amber-100 text-amber-700" : "bg-[var(--brand)]/10 text-[var(--brand)]"}`}>
              {(post.author_name || "?")[0]}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-sm capitalize truncate">{post.author_name}</p>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0 ${tag.cls}`} data-testid={`badge-${post.kind}`}>
                <TagIcon size={10} /> {tag.label}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <p className="text-[10px] uppercase tracking-wider text-gray-400">{timeAgo(post.created_at)}</p>
              {post.location && <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500"><MapPin size={9} /> {post.location}</span>}
              {post.event_tag && <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600 font-medium"><Tag size={9} /> {post.event_tag}</span>}
            </div>
          </div>
        </div>
      </div>

      {post.content && <p className="text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{post.content}</p>}

      {post.media_type === "image" && post.media_url && (
        <img src={mediaSrc} alt="" className="w-full rounded-lg border border-[var(--border)] max-h-[460px] object-cover" data-testid="post-image" />
      )}
      {post.media_type === "video" && post.media_url && (
        <video src={mediaSrc} controls playsInline className="w-full rounded-lg border border-[var(--border)] max-h-[480px] bg-black" data-testid="post-video-upload" />
      )}
      {(post.media_type === "youtube" || post.media_type === "vimeo") && post.media_url && (
        <div className="aspect-video rounded-lg overflow-hidden bg-black" data-testid={`post-video-${post.media_type}`}>
          <iframe src={post.media_url} title="video" className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
        </div>
      )}

      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <button
          type="button"
          onClick={() => onReact?.(post.id, "up")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all active:scale-95 ${r.my_up ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-white border-[var(--border)] text-gray-600 hover:border-blue-300 hover:text-blue-600"}`}
          data-testid={`react-up-${post.id}`}
        >
          <ThumbsUp size={14} strokeWidth={2.25} className={r.my_up ? "fill-blue-600 text-blue-600" : ""} />
          <span className="tabular-nums">{r.up || 0}</span>
          <span className="hidden sm:inline">Like</span>
        </button>
        <button
          type="button"
          onClick={() => onReact?.(post.id, "heart")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all active:scale-95 ${r.my_heart ? "bg-pink-100 border-pink-300 text-pink-700" : "bg-white border-[var(--border)] text-gray-600 hover:border-pink-300 hover:text-pink-600"}`}
          data-testid={`react-heart-${post.id}`}
        >
          <Heart size={14} strokeWidth={2.25} className={r.my_heart ? "fill-pink-600 text-pink-600" : ""} />
          <span className="tabular-nums">{r.heart || 0}</span>
          <span className="hidden sm:inline">Love</span>
        </button>
        {!adminMode && (
          <button
            type="button"
            onClick={() => setShowComments((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border bg-white border-[var(--border)] text-gray-600 hover:border-gray-400 transition-all"
            data-testid={`toggle-comments-${post.id}`}
          >
            <MessageCircle size={14} />
            <span className="tabular-nums">{post.comment_count || 0}</span>
            <span className="hidden sm:inline">Comment{(post.comment_count || 0) === 1 ? "" : "s"}</span>
          </button>
        )}
        {!adminMode && (
          <div className="ml-auto flex items-center gap-2">
            <ShareButtons text={post.content || post.author_name} />
            {isHQ && (post.kind === "review" || post.kind === "post") && (
              <button
                type="button"
                onClick={() => onFeature?.(post.id)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${post.is_featured ? "bg-[#D4AF37] text-[#0F0F10] border-[#D4AF37]" : "bg-white text-gray-500 border-[var(--border)] hover:border-[#D4AF37] hover:text-[#8a6d16]"}`}
                data-testid={`feature-${post.id}`}
              >
                <Award size={11} /> {post.is_featured ? "Unfeature" : "Feature"}
              </button>
            )}
          </div>
        )}
      </div>

      {!adminMode && showComments && <Comments post={post} identity={identity} onNeedLogin={onNeedLogin} onChanged={onChanged} />}

      {adminMode && (
        <div className="flex gap-2 pt-2 border-t border-[var(--border)]">
          <Button size="sm" onClick={() => onApprove(post.id)} className="btn-brand flex-1" data-testid={`approve-${post.id}`}>
            <CheckCircle2 size={13} className="mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReject(post.id)} className="text-red-600 hover:bg-red-50" data-testid={`reject-${post.id}`}>
            <X size={13} className="mr-1" /> Reject
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------- Event submission form ----------
function EventForm({ onClose, onSubmitted }) {
  const [f, setF] = useState({
    title: "", full_name: "", email: "", phone: "", location: "", start_datetime: "", end_datetime: "",
    audience: "", description: "", category: "", expected_attendance: "", website_url: "", social_handles: "", accessibility_notes: "",
  });
  const [image, setImage] = useState(null);
  const [hp, setHp] = useState("");  // honeypot
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const req = ["title", "full_name", "email", "phone", "location", "start_datetime", "audience"];
    for (const k of req) if (!f[k].trim()) { toast.error("Please complete all required fields"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("website", hp);  // honeypot — bots fill it, humans don't
      if (image) fd.append("image", image);
      await axios.post(`${API}/api/events`, fd);
      toast.success("Event submitted! It'll appear once an admin approves it.");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="event-form-modal">
      <Card className="relative w-full max-w-2xl my-4 p-5 sm:p-6 space-y-4 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700" data-testid="event-form-close"><X size={18} /></button>
        <div className="flex items-center gap-2">
          <CalendarDays size={18} className="text-[var(--brand)]" />
          <div>
            <p className="font-bold text-base">Submit an Event</p>
            <p className="text-xs text-gray-500">Anyone can submit. Events go live after admin approval.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Event name *</Label><Input value={f.title} onChange={set("title")} placeholder="e.g. Fall Natural Hair Meetup" data-testid="event-title" /></div>
          {/* Honeypot — visually hidden; real users never see or fill it */}
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} data-testid="event-hp" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Your full name *</Label><Input value={f.full_name} onChange={set("full_name")} data-testid="event-fullname" /></div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="event-email" /></div>
            <div><Label className="text-xs">Phone *</Label><Input value={f.phone} onChange={set("phone")} data-testid="event-phone" /></div>
            <div><Label className="text-xs">Intended audience *</Label><Input value={f.audience} onChange={set("audience")} placeholder="e.g. Stylists, students" data-testid="event-audience" /></div>
          </div>
          <div><Label className="text-xs">Location *</Label><Input value={f.location} onChange={set("location")} placeholder="Venue & address" data-testid="event-location" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Starts *</Label><Input type="datetime-local" value={f.start_datetime} onChange={set("start_datetime")} data-testid="event-start" /></div>
            <div><Label className="text-xs">Ends</Label><Input type="datetime-local" value={f.end_datetime} onChange={set("end_datetime")} data-testid="event-end" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Category</Label><Input value={f.category} onChange={set("category")} placeholder="Hair Show, Class, Expo…" data-testid="event-category" /></div>
            <div><Label className="text-xs">Expected attendance</Label><Input value={f.expected_attendance} onChange={set("expected_attendance")} placeholder="e.g. 200+" data-testid="event-attendance" /></div>
          </div>
          <div><Label className="text-xs">Description</Label><Textarea rows={3} value={f.description} onChange={set("description")} placeholder="Tell people what to expect…" data-testid="event-description" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Website / registration link</Label><Input value={f.website_url} onChange={set("website_url")} placeholder="https://…" data-testid="event-website" /></div>
            <div><Label className="text-xs">Organizer social handles</Label><Input value={f.social_handles} onChange={set("social_handles")} placeholder="@yourbrand" data-testid="event-social" /></div>
          </div>
          <div><Label className="text-xs">Accessibility notes</Label><Input value={f.accessibility_notes} onChange={set("accessibility_notes")} placeholder="Wheelchair access, ASL, parking…" data-testid="event-accessibility" /></div>
          <div>
            <Label className="text-xs flex items-center gap-1"><ImageIcon size={11} /> Event image (adds to the gallery)</Label>
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="block w-full text-xs mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[var(--brand)]/10 file:text-[var(--brand)] file:text-xs file:font-semibold" data-testid="event-image" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={busy} className="btn-brand flex-1" data-testid="event-submit">{busy ? "Submitting…" : "Submit event"}</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ---------- Event card (upcoming list / lightbox) ----------
function EventCard({ ev, onRsvp }) {
  const b = dayBadge(ev.start_datetime);
  const img = abs(ev.image_url);
  return (
    <div className="flex gap-3" data-testid={`event-card-${ev.id}`}>
      <div className="flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-[var(--brand)] text-white shrink-0">
        <span className="text-[9px] font-bold tracking-wider">{b.m}</span>
        <span className="text-lg font-bold leading-none">{b.day}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm text-[var(--ink)] leading-tight">{ev.title}</p>
        <p className="text-[11px] text-gray-500 mt-0.5 inline-flex items-center gap-1"><Clock size={10} /> {fmtEventDate(ev.start_datetime)}</p>
        <p className="text-[11px] text-gray-500 inline-flex items-start gap-1"><MapPin size={10} className="mt-0.5 shrink-0" /> <span className="line-clamp-1">{ev.location}</span></p>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {ev.category && <span className="inline-flex px-1.5 py-0.5 rounded bg-[var(--brand)]/10 text-[var(--brand)] text-[9px] font-bold uppercase tracking-wide">{ev.category}</span>}
          {ev.website_url && <a href={ev.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600 hover:underline" data-testid={`event-link-${ev.id}`}><Link2 size={9} /> Tickets</a>}
          {(ev.rsvp_count || 0) > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium"><Users size={9} /> {ev.rsvp_count} going</span>}
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {onRsvp && (
            <button onClick={() => onRsvp(ev)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--brand)] text-white text-[11px] font-semibold hover:bg-[var(--brand-hover)] transition-colors active:scale-95" data-testid={`rsvp-btn-${ev.id}`}>
              <CheckCircle2 size={11} /> RSVP
            </button>
          )}
          <a href={`${API}/api/events/${ev.id}/ics`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[var(--border)] text-gray-600 text-[11px] font-semibold hover:border-[var(--brand)]/40 hover:text-[var(--brand)] transition-colors" data-testid={`ics-btn-${ev.id}`}>
            <CalendarPlus size={11} /> Add to Calendar
          </a>
        </div>
      </div>
      {img && <img src={img} alt="" className="hidden sm:block w-16 h-16 rounded-lg object-cover border border-[var(--border)] shrink-0" />}
    </div>
  );
}

// ---------- Community Calendar sidebar ----------
function CalendarSidebar({ events, onAdd, onRsvp }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const eventDays = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = new Date(e.start_datetime);
      if (isNaN(d)) return;
      if (d.getFullYear() === cursor.y && d.getMonth() === cursor.m) map[d.getDate()] = true;
    });
    return map;
  }, [events, cursor]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const monthLabel = first.toLocaleString(undefined, { month: "long", year: "numeric" });
  const today = new Date();
  const isToday = (d) => today.getFullYear() === cursor.y && today.getMonth() === cursor.m && today.getDate() === d;

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const nav = (delta) => setCursor((c) => {
    let m = c.m + delta, y = c.y;
    if (m < 0) { m = 11; y -= 1; } if (m > 11) { m = 0; y += 1; }
    return { y, m };
  });

  const upcoming = events.slice(0, 5);

  return (
    <div className="space-y-5">
      <Card className="p-4 rounded-2xl border border-[#B4B8C0] shadow-sm" data-testid="community-calendar">
        <div className="flex items-center justify-between mb-3">
          <p className="font-bold text-sm uppercase tracking-wide inline-flex items-center gap-1.5"><CalendarDays size={15} className="text-[var(--brand)]" /> Community Calendar</p>
          <Button size="sm" onClick={onAdd} className="btn-brand h-7 px-2.5 text-[11px]" data-testid="calendar-add-event"><Plus size={12} className="mr-0.5" /> Add</Button>
        </div>
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => nav(-1)} className="p-1 rounded hover:bg-[var(--bg-soft)] text-gray-500" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <p className="text-sm font-semibold text-[var(--ink)]">{monthLabel}</p>
          <button onClick={() => nav(1)} className="p-1 rounded hover:bg-[var(--bg-soft)] text-gray-500" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-[10px] font-bold text-gray-400 py-1">{d}</div>)}
          {cells.map((d, i) => (
            <div key={i} className="aspect-square flex flex-col items-center justify-center text-xs relative">
              {d && (
                <div className={`w-7 h-7 flex items-center justify-center rounded-full ${isToday(d) ? "bg-[var(--brand)] text-white font-bold" : eventDays[d] ? "text-[var(--ink)] font-semibold" : "text-gray-500"}`}>
                  {d}
                </div>
              )}
              {d && eventDays[d] && !isToday(d) && <span className="absolute bottom-0.5 w-1.5 h-1.5 rounded-full bg-[var(--brand)]" data-testid={`cal-dot-${d}`} />}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 rounded-2xl border border-[#B4B8C0] shadow-sm" data-testid="upcoming-events">
        <p className="font-bold text-sm uppercase tracking-wide mb-3">Upcoming Events</p>
        {upcoming.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No upcoming events yet — add one!</p>
        ) : (
          <div className="space-y-4">
            {upcoming.map((ev) => <EventCard key={ev.id} ev={ev} onRsvp={onRsvp} />)}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------- Featured tile ----------
function FeaturedTile({ tile }) {
  if (!tile) return null;
  return (
    <div className="group relative rounded-xl overflow-hidden border border-[#B4B8C0]/40 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 aspect-[16/11]" data-testid="featured-tile">
      <img src={tile.img} alt={tile.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--brand)] text-white text-[9px] font-bold uppercase tracking-wider mb-1.5">{tile.tag}</span>
        <p className="font-bold text-base leading-tight">{tile.title}</p>
        {tile.desc && <p className="text-[11px] text-white/80 mt-0.5 line-clamp-2">{tile.desc}</p>}
        {tile.date && <p className="text-[10px] text-[#F5D77E] font-semibold uppercase tracking-wide mt-1.5">{tile.date}</p>}
      </div>
    </div>
  );
}

// ================= Main page =================
export default function EssencePage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [pending, setPending] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState({ verified: false, is_staff: false });
  const [clientSession, setClientSession] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const composeRef = useRef(null);

  const isHQ = !!user && (user.role === "admin" || user.role === "super_admin" || user.role === "personnel");

  const loadIdentity = async () => {
    const version = authRevision();
    if (requiresFreshLogin()) { setIdentity({ verified: false, is_staff: false }); return; }
    try {
      const { data } = await axios.get(`${API}/api/essence/me`, { headers: communityHeaders(), withCredentials: true });
      if (version === authRevision()) setIdentity(data);
    } catch { setIdentity({ verified: false, is_staff: false }); }
  };
  const load = async () => {
    setLoading(true);
    try {
      const anon = getAnonId();
      const { data } = await axios.get(`${API}/api/essence?anon_id=${encodeURIComponent(anon)}`, { headers: communityHeaders(), withCredentials: true });
      setPosts(data);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  };
  const loadEvents = async () => {
    try {
      const [e, g] = await Promise.all([axios.get(`${API}/api/events`), axios.get(`${API}/api/events/gallery`)]);
      setEvents(e.data); setGallery(g.data);
    } catch { setEvents([]); setGallery([]); }
  };
  const loadTeam = async () => {
    try { const { data } = await axios.get(`${API}/api/team`); setTeam(data); }
    catch { setTeam([]); }
  };
  const loadPending = async () => {
    if (!isHQ) return;
    try { const { data } = await api.get("/admin/essence/pending"); setPending(data); }
    catch { setPending([]); }
  };

  useEffect(() => {
    load(); loadEvents(); loadTeam(); loadIdentity();
    const tk = localStorage.getItem("client_token");
    const version = authRevision();
    if (tk) {
      axios.get(`${API}/api/client/me`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => { if (version === authRevision()) setClientSession(r.data); })
        .catch(() => { localStorage.removeItem("client_token"); localStorage.removeItem("client_id"); });
    }
  }, []); // eslint-disable-line
  useEffect(() => { loadPending(); loadIdentity(); }, [user]); // eslint-disable-line
  useEffect(() => {
    const cleared = () => {
      setIdentity({ verified: false, is_staff: false });
      setClientSession(null);
      setPending([]);
    };
    window.addEventListener(AUTH_CLEARED_EVENT, cleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, cleared);
  }, []);

  const approve = async (id) => { await api.post(`/admin/essence/${id}/approve`, {}); toast.success("Approved"); await Promise.all([load(), loadPending()]); };
  const reject = async (id) => { await api.post(`/admin/essence/${id}/reject`, {}); toast.success("Rejected"); await loadPending(); };
  const react = async (postId, emoji) => {
    try {
      await axios.post(`${API}/api/essence/${postId}/react`, { emoji, anon_id: getAnonId() }, { headers: communityHeaders(), withCredentials: true });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reaction failed"); }
  };
  const feature = async (postId) => {
    try {
      const { data } = await api.post(`/admin/essence/${postId}/feature`, {});
      toast.success(data.is_featured ? "Pinned as featured" : "Unfeatured");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feature failed"); }
  };
  const needLogin = () => { setShowLogin(true); setTimeout(() => composeRef.current?.scrollIntoView({ behavior: "smooth" }), 100); };

  // Featured tiles — event + team spotlight + featured/trending post
  const featured = useMemo(() => {
    const tiles = [];
    if (events[0]) tiles.push({ tag: "Featured Event", title: events[0].title, desc: events[0].description || events[0].location, date: shortDate(events[0].start_datetime), img: abs(events[0].image_url) || FEA_EVENT });
    if (team[0]) tiles.push({ tag: "Team Spotlight", title: team[0].name, desc: team[0].title || "MPIRE Team", date: team[0].title || "Spotlight", img: abs(team[0].photo_url) || FEA_TEAM });
    const fp = posts.find((p) => p.is_featured) || events[1];
    if (fp) {
      if (fp.title) tiles.push({ tag: "Upcoming", title: fp.title, desc: fp.description || fp.location, date: shortDate(fp.start_datetime), img: abs(fp.image_url) || FEA_TREND });
      else tiles.push({ tag: "Trending", title: (fp.content || "Community story").slice(0, 48), desc: (fp.content || "").slice(0, 90), date: timeAgo(fp.created_at), img: (fp.media_type === "image" && abs(fp.media_url)) || FEA_TREND });
    }
    return tiles.slice(0, 3);
  }, [events, team, posts]);

  const filteredPosts = useMemo(() => {
    if (filter === "all") return posts;
    if (filter === "events") return posts.filter((p) => p.kind === "announcement" || p.event_tag);
    if (filter === "testimonials") return posts.filter((p) => p.kind === "review");
    if (filter === "videos") return posts.filter((p) => ["video", "youtube", "vimeo"].includes(p.media_type));
    return posts;
  }, [posts, filter]);

  const filters = [
    { id: "all", label: "All" },
    { id: "events", label: "Events" },
    { id: "testimonials", label: "Testimonials" },
    { id: "videos", label: "Videos" },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-soft)]">
      {/* Top bar */}
      <header className="bg-white border-b border-[var(--border)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/shop" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[var(--brand)]" data-testid="essence-back">
            <ArrowLeft size={14} /> Store
          </Link>
          <div className="flex items-center gap-2 min-w-0">
            <img src="/brand/mpire-logo.png" alt="MPIRE" className="w-7 h-7 object-contain shrink-0" />
            <span className="font-bold text-sm tracking-tight hidden sm:inline">MPIRE Distribution</span>
          </div>
          <div className="w-16 text-right">
            {clientSession && <span className="text-[10px] uppercase tracking-wider text-gray-500" data-testid="essence-client-badge">{clientSession.name?.split(" ")[0]}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        {/* ── HERO ── */}
        <section className="relative w-full rounded-2xl overflow-hidden border border-[#2A2A2F] shadow-2xl" data-testid="essence-hero">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F0F10] via-[#1A1A1D]/95 to-[#2b0d10]/90" />
          <div className="relative p-6 sm:p-10 lg:p-14 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm mb-4">
              <span className="w-2 h-2 rounded-full bg-[var(--brand)] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]">The Community Hub</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none" data-testid="essence-heading">
              The Essence of <span className="text-[var(--brand)]">MP</span><span className="text-[#D4AF37]">ire</span>
            </h1>
            <p className="text-sm sm:text-base text-white/75 mt-3 max-w-2xl leading-relaxed">
              The wall where the MPIRE family gathers — share looks, wins & reviews, add community events across the DMV & Atlanta, and celebrate the craft we live for.
            </p>
            <div className="flex flex-wrap gap-2.5 mt-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-xs font-semibold"><Users size={13} className="text-[#D4AF37]" /> {team.length}+ Partners</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-xs font-semibold"><CalendarDays size={13} className="text-[#D4AF37]" /> {events.length} Events</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-xs font-semibold"><Sparkles size={13} className="text-[#D4AF37]" /> {posts.length} Posts</span>
            </div>
          </div>
        </section>

        {/* ── FEATURED TILES ── */}
        {featured.length > 0 && (
          <section data-testid="featured-tiles">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-[var(--brand)]" />
              <p className="label-eyebrow text-[var(--brand)]">Highlights</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {featured.map((t, i) => <FeaturedTile key={i} tile={t} />)}
            </div>
          </section>
        )}

        {/* ── TWO-COLUMN: feed + calendar ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" ref={composeRef}>
          {/* Feed column */}
          <div className="lg:col-span-8 space-y-5">
            {identity.verified ? (
              <ComposeForm onPosted={load} />
            ) : (
              showLogin ? (
                <InlineClientLogin onLoggedIn={async (c) => { setClientSession(c); setShowLogin(false); await loadIdentity(); }} onClose={() => setShowLogin(false)} />
              ) : (
                <Card className="p-4 rounded-2xl border border-dashed border-[var(--brand)]/40 bg-white flex items-center justify-between gap-3" data-testid="composer-locked">
                  <p className="text-sm text-gray-600"><span className="font-semibold text-[var(--ink)]">Join the conversation.</span> Sign in to post, comment & share.</p>
                  <Button onClick={needLogin} className="btn-brand shrink-0" data-testid="composer-signin"><ShieldCheck size={14} className="mr-1.5" /> Sign in</Button>
                </Card>
              )
            )}

            {/* HQ controls */}
            {isHQ && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => { setShowAnnouncementForm((v) => !v); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="essence-create-announcement">
                  <Megaphone size={14} className="mr-1.5" /> Announcement
                </Button>
                <Button variant="outline" onClick={() => { setShowPending(!showPending); loadPending(); }} data-testid="essence-review-queue">
                  <Clock size={14} className="mr-1.5" /> Pending ({pending.length})
                </Button>
              </div>
            )}
            {showAnnouncementForm && isHQ && (
              <AnnouncementForm onCancel={() => setShowAnnouncementForm(false)} onPosted={() => { setShowAnnouncementForm(false); load(); }} />
            )}
            {isHQ && showPending && (
              <div className="space-y-3" data-testid="pending-review-list">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Awaiting your approval</p>
                {pending.length === 0 ? <p className="text-sm text-gray-500 italic">Nothing pending.</p> :
                  pending.map((p) => <PostCard key={p.id} post={p} adminMode onApprove={approve} onReject={reject} isHQ={isHQ} identity={identity} />)}
              </div>
            )}

            {/* Filter bar */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 border-b border-[#B4B8C0]/40" data-testid="feed-filter-bar">
              {filters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-all ${filter === f.id ? "bg-[var(--brand)] text-white shadow" : "bg-white text-[var(--ink-soft)] border border-[#B4B8C0]/60 hover:border-[var(--brand)]/40"}`}
                  data-testid={`filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Feed */}
            <div className="space-y-4">
              {loading ? (
                <p className="text-sm text-gray-500 text-center py-10">Loading feed…</p>
              ) : filteredPosts.length === 0 ? (
                <Card className="p-10 text-center text-gray-500 rounded-xl" data-testid="essence-empty">No posts here yet. Be the first to share!</Card>
              ) : (
                filteredPosts.map((p) => <PostCard key={p.id} post={p} onReact={react} onFeature={feature} isHQ={isHQ} identity={identity} onNeedLogin={needLogin} onChanged={load} />)
              )}
            </div>
          </div>

          {/* Calendar sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-20">
            <CalendarSidebar events={events} onAdd={() => setShowEventForm(true)} onRsvp={(ev) => setRsvpEvent(ev)} />
          </aside>
        </div>

        {/* ── EVENT GALLERY ── */}
        <section data-testid="event-gallery">
          <div className="flex items-center gap-2 mb-3">
            <Images size={16} className="text-[var(--brand)]" />
            <p className="label-eyebrow text-[var(--brand)]">Event Gallery</p>
          </div>
          {gallery.length === 0 ? (
            <Card className="p-10 text-center text-gray-500 rounded-xl" data-testid="gallery-empty">
              <Images size={28} className="mx-auto mb-2 text-gray-300" />
              No event photos yet. Approved events with images show up here.
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
              {gallery.map((ev) => (
                <button key={ev.id} onClick={() => setLightbox(ev)} className="group relative aspect-square rounded-lg overflow-hidden border border-[var(--border)]" data-testid={`gallery-item-${ev.id}`}>
                  <img src={abs(ev.image_url)} alt={ev.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-[11px] font-semibold text-left leading-tight line-clamp-2">{ev.title}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* ── MEET THE TEAM ── */}
        {team.length > 0 && (
          <section data-testid="meet-the-team-section">
            <div className="flex items-center gap-2 mb-1">
              <Award size={16} className="text-[#D4AF37]" />
              <p className="label-eyebrow text-[#8a6d16]">Meet The Team</p>
            </div>
            <p className="text-sm text-gray-500 mb-4 max-w-2xl">The people who move MPIRE forward — our business development, team members and distributing partners across the DMV & beyond.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="team-grid">
              {team.map((m) => {
                const titleCls = m.title === "Business Developer"
                  ? "bg-[#0F0F10] text-[#D4AF37]"
                  : m.title === "Distributing Member"
                  ? "bg-[var(--brand)]/10 text-[var(--brand)]"
                  : "bg-[#D4AF37]/15 text-[#8a6d16]";
                return (
                  <Card key={m.id} className="p-6 flex flex-col items-center text-center rounded-2xl border border-[#B4B8C0] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`team-card-${m.id}`}>
                    {/* Name ABOVE photo */}
                    <p className="text-lg font-bold capitalize text-[var(--ink)] leading-tight" data-testid={`team-name-${m.id}`}>{m.name}</p>
                    <span className={`mt-1.5 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${titleCls}`} data-testid={`team-title-${m.id}`}>
                      <Award size={10} /> {m.title}
                    </span>
                    {/* Photo centered */}
                    <div className="mt-4 w-28 h-28 rounded-full overflow-hidden border-4 shadow-md flex items-center justify-center text-3xl bg-[var(--bg-soft)]" style={{ borderColor: m.color || "#D4AF37" }}>
                      {m.photo_url ? (
                        <img src={abs(m.photo_url)} alt={m.name} className="w-full h-full object-cover" data-testid={`team-photo-${m.id}`} />
                      ) : m.photo_pending ? (
                        <div className="flex flex-col items-center justify-center text-gray-400" data-testid={`team-photo-pending-${m.id}`}>
                          <Loader2 size={20} className="animate-spin" />
                          <span className="text-[8px] mt-1 uppercase tracking-wide">Portrait</span>
                        </div>
                      ) : (
                        <span className="font-bold text-gray-400" style={{ color: m.color }}>{(m.name || "?")[0]}</span>
                      )}
                    </div>
                    {/* Edification / quote below photo */}
                    <p className="mt-4 text-sm text-[var(--ink-soft)] italic leading-relaxed min-h-[3.5rem] flex items-center" data-testid={`team-quote-${m.id}`}>
                      &ldquo;{m.quote}&rdquo;
                    </p>
                    {/* Coverage at the bottom */}
                    {m.coverage && (
                      <div className="mt-3 pt-3 border-t border-[var(--border)] w-full">
                        <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{m.coverage_label || "Coverage Area"}</p>
                        <p className="text-xs font-medium text-[var(--brand)] mt-0.5 inline-flex items-center gap-1" data-testid={`team-coverage-${m.id}`}>
                          <MapPin size={11} /> {m.coverage}
                        </p>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {showEventForm && <EventForm onClose={() => setShowEventForm(false)} onSubmitted={() => { setShowEventForm(false); loadEvents(); }} />}

      {rsvpEvent && <RsvpModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} onDone={() => loadEvents()} />}

      {lightbox && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow text-gray-700" data-testid="lightbox-close"><X size={16} /></button>
            <img src={abs(lightbox.image_url)} alt={lightbox.title} className="w-full rounded-t-xl max-h-[65vh] object-contain bg-black" />
            <div className="bg-white rounded-b-xl p-4 space-y-3">
              <EventCard ev={lightbox} onRsvp={(ev) => { setRsvpEvent(ev); }} />
              <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Share this event</span>
                <ShareButtons text={lightbox.title} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- HQ announcement composer ----------
function AnnouncementForm({ onCancel, onPosted }) {
  const [content, setContent] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) { toast.error("Content is required"); return; }
    setBusy(true);
    try {
      await api.post("/essence/announcements", { content: content.trim(), media_url: mediaUrl.trim() || null });
      toast.success("Announcement published");
      setContent(""); setMediaUrl(""); onPosted();
    } catch (err) { toast.error(err.response?.data?.detail || "Post failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 sm:p-5 space-y-3 border-2 border-amber-400 bg-amber-50/40 rounded-2xl" data-testid="compose-announcement">
      <div className="flex items-center gap-2"><Megaphone size={16} className="text-amber-600" /><p className="font-semibold text-sm">Create Announcement</p></div>
      <form onSubmit={submit} className="space-y-3">
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="Share news, an event, or a company update…" data-testid="announcement-content" required />
        <Input value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="Image / YouTube / Vimeo link (optional)" data-testid="announcement-media" />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy} className="btn-brand flex-1" data-testid="announcement-submit">{busy ? "Publishing…" : "Publish now"}</Button>
          <Button type="button" variant="outline" onClick={onCancel} data-testid="announcement-cancel">Cancel</Button>
        </div>
      </form>
    </Card>
  );
}
